import { useCallback, useEffect } from "react";
import { TimberType, CreateTimberType, UpdateTimberType } from "../types/models/TimberType.d";
import { UpdateEntityTimberType } from "../types/models/TimberType";
import { useGetTimberTypeListQuery, useInsertTimberTypeMutation, useUpdateTimberTypeMutation, useDeleteTimberTypeMutation, } from "../apis/TimberType.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useTimberType(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertTimberTypeMutation, { 
        isLoading: insertTimberTypeLoading, 
        isSuccess: insertTimberTypeIsSuccess,
        isError: insertTimberTypeIsError,
        error: insertTimberTypeError,
    }] = useInsertTimberTypeMutation()
    const [updateTimberTypeMutation, { 
        isLoading: updateTimberTypeLoading,
        isSuccess: updateTimberTypeIsSuccess,
        isError: updateTimberTypeIsError,
        error: updateTimberTypeError,
    }] = useUpdateTimberTypeMutation()
    const [deleteTimberTypeMutation, { 
        isLoading: deleteTimberTypeLoading,
        isSuccess: deleteTimberTypeIsSuccess,
        isError: deleteTimberTypeIsError,
        error: deleteTimberTypeError,
    }] = useDeleteTimberTypeMutation()

    const loading =
        insertTimberTypeLoading ||
        updateTimberTypeLoading ||
        deleteTimberTypeLoading ||
        false

    const insertTimberType = useCallback(async (timberType: CreateTimberType, redirectPath: string|undefined) => {
        try {
            const data = await insertTimberTypeMutation(timberType).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertTimberTypeMutation, navigate])
    
    useEffect(() => {
        if (insertTimberTypeIsSuccess) {
            openSuccessAlert('Timber Type created')
        } else if (insertTimberTypeIsError) {
            console.log("add error", insertTimberTypeError)
            openFailAlert(t`Timber Type add error: ` + insertTimberTypeError?.data?.message)
        }
    }, [
        insertTimberTypeIsSuccess,
        insertTimberTypeIsError,
        insertTimberTypeError
    ]);

    const updateTimberType = useCallback(async (timberType: UpdateEntityTimberType, redirectPath: string|undefined) => {
        try {
            const data = await updateTimberTypeMutation(timberType).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateTimberTypeMutation, navigate])
    
    useEffect(() => {
        if (updateTimberTypeIsSuccess) {
            openSuccessAlert(t`Timber Type updated`)
        } else if (updateTimberTypeIsError) {
            console.log("update error", updateTimberTypeError)
            openFailAlert(t`Timber Type update error: ` + updateTimberTypeError?.data?.message)
        }
    }, [
        updateTimberTypeIsSuccess,
        updateTimberTypeIsError,
        updateTimberTypeError
    ]);

    const deleteTimberType = useCallback((timberType: TimberType, redirectPath: string|undefined) => {
        openDialog(async () => {
            timberType = {
                ...timberType,
            }
            try {
                const { data } = await deleteTimberTypeMutation(timberType);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteTimberTypeMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteTimberTypeIsSuccess) {
            openSuccessAlert(t`Timber Type deleted`)
        } else if (deleteTimberTypeIsError) {
            console.log("delete error", deleteTimberTypeError)
            openFailAlert(t`Timber Type delete error: ` + deleteTimberTypeError?.data?.message)
        }
    }, [
        deleteTimberTypeIsSuccess,
        deleteTimberTypeIsError,
        deleteTimberTypeError
    ]);

    return {
        loading,
        insertTimberType,
        updateTimberType,
        deleteTimberType
    }
}
